// En desarrollo local, usar URL completa del backend. En producción (Vercel), usar ruta relativa
const getMessagesApiUrl = () => {
  // Si hay una URL configurada explícitamente, usarla (útil para desarrollo local)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // Por defecto, usar /api (mismo comportamiento que api.ts)
  return '/api';
};
const API_URL = getMessagesApiUrl();
export interface Message {
  id: number;
  message: string;
  read: boolean;
  createdAt: string;
  attachmentUrl?: string;
  attachmentFilename?: string;
  attachmentType?: string;
  edited?: boolean;
  deleted?: boolean;
  replyToMessageId?: number;
  delivered?: boolean;
  seenAt?: string;
  fromUser: {
    id: number;
    email: string;
    role: string;
    name?: string;
  };
}
export interface MessagesResponse {
  success: boolean;
  messages: Message[];
  unreadCount: number;
}
export interface SendMessageRequest {
  toUserId: number;
  message?: string;
  attachmentUrl?: string;
  attachmentFilename?: string;
  attachmentType?: string;
  replyToMessageId?: number;
  /** Si true, el mensaje se guarda como enviado por el admin (solo editores con permiso mensajería) */
  sendAsSupport?: boolean;
}
export interface User {
  id: number;
  email: string;
  role: string;
  createdAt: string;
}
export interface UsersResponse {
  success: boolean;
  users: User[];
}
/**
 * Obtener mensajes del usuario actual
 */
export async function getMessages(): Promise<MessagesResponse> {
  const response = await fetch(`${API_URL}/messages/get`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Error al obtener mensajes' }));
    throw new Error(error.error || 'Error al obtener mensajes');
  }
  return response.json();
}
/**
 * Enviar mensaje (solo admins)
 */
export async function sendMessage(data: SendMessageRequest): Promise<{ success: boolean; message: string; data: any }> {
  const response = await fetch(`${API_URL}/messages/send`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Error al enviar mensaje' }));
    throw new Error(error.error || 'Error al enviar mensaje');
  }
  return response.json();
}
/**
 * Marcar mensaje como leído
 */
export async function markMessageAsRead(messageId: number): Promise<{ success: boolean; message: string }> {
  const response = await fetch(`${API_URL}/messages/mark-read/${messageId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Error al marcar mensaje como leído' }));
    throw new Error(error.error || 'Error al marcar mensaje como leído');
  }
  return response.json();
}
/**
 * Obtener lista de usuarios (solo admins)
 */
export async function getUsers(): Promise<UsersResponse> {
  const response = await fetch(`${API_URL}/messages/users`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Error al obtener usuarios' }));
    throw new Error(error.error || 'Error al obtener usuarios');
  }
  return response.json();
}
export interface Conversation {
  userId: number;
  email: string;
  role: string;
  name?: string;
  unreadCount: number;
  lastMessage: string;
  lastMessageDate: string;
  lastMessageFromUserId?: number;
}
export interface ConversationsResponse {
  success: boolean;
  conversations: Conversation[];
}
export interface ConversationMessage {
  id: number;
  message: string;
  read: boolean;
  createdAt: string;
  attachmentUrl?: string;
  attachmentFilename?: string;
  attachmentType?: string;
  edited?: boolean;
  deleted?: boolean;
  replyToMessageId?: number;
  delivered?: boolean;
  seenAt?: string;
  fromUserId: number;
  isFromCurrentUser: boolean;
  fromUser: {
    id: number;
    email: string;
    role: string;
    name?: string;
  };
}
export interface ConversationResponse {
  success: boolean;
  otherUser: {
    id: number;
    email: string;
    role: string;
    name?: string;
    firstName?: string | null;
    lastName?: string | null;
    organizationName?: string | null;
    position?: string | null;
    profileId?: number | null;
  };
  messages: ConversationMessage[];
}
/**
 * Obtener todas las conversaciones del usuario actual
 */
export async function getConversations(): Promise<ConversationsResponse> {
  const response = await fetch(`${API_URL}/messages/conversations`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Error al obtener conversaciones' }));
    throw new Error(error.error || 'Error al obtener conversaciones');
  }
  return response.json();
}
/**
 * Obtener el ID del admin (para usuarios que quieren iniciar una conversación)
 */
export async function getAdmin(): Promise<{ success: boolean; admin: { id: number; email: string; name: string } }> {
  const response = await fetch(`${API_URL}/messages/admin`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Error al obtener administrador' }));
    throw new Error(error.error || 'Error al obtener administrador');
  }
  return response.json();
}
/**
 * Obtener mensajes de una conversación específica
 */
export async function getConversation(otherUserId: number): Promise<ConversationResponse> {
  const response = await fetch(`${API_URL}/messages/conversation/${otherUserId}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Error al obtener conversación' }));
    throw new Error(error.error || 'Error al obtener conversación');
  }
  return response.json();
}
/**
 * Editar mensaje
 */
export async function editMessage(messageId: number, message: string): Promise<{ success: boolean; message: string }> {
  const response = await fetch(`${API_URL}/messages/edit/${messageId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ message }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Error al editar mensaje' }));
    throw new Error(error.error || 'Error al editar mensaje');
  }
  return response.json();
}
/**
 * Eliminar mensaje
 */
export async function deleteMessage(messageId: number): Promise<{ success: boolean; message: string }> {
  const response = await fetch(`${API_URL}/messages/delete/${messageId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Error al eliminar mensaje' }));
    throw new Error(error.error || 'Error al eliminar mensaje');
  }
  return response.json();
}
/**
 * Descargar archivo adjunto de un mensaje
 */
export async function downloadMessageAttachment(messageId: number, filename: string): Promise<Blob> {
  const response = await fetch(`${API_URL}/messages/download/${messageId}`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Error al descargar archivo' }));
    throw new Error(error.error || 'Error al descargar archivo');
  }
  return response.blob();
}